import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  CreditCard,
  Smartphone,
  Building2,
  ArrowRight,
  Lock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Copy,
  ExternalLink,
  Banknote,
} from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import api from "../utils/api";
import toast from "react-hot-toast";
import "./CheckoutPage.css";

// Stripe singleton — never re-created on re-render
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLIC_KEY || "pk_test_placeholder",
);

// ─── Payment method definitions ───────────────────────────────────────────────

const PAYMENT_METHODS = [
  {
    id: "stripe_card",
    label: "Credit / Debit Card",
    icon: <CreditCard size={20} />,
    desc: "Visa, Mastercard, Amex — instant",
    redirect: false,
    badge: null,
  },
  {
    id: "twint",
    label: "TWINT",
    icon: <Smartphone size={20} />,
    desc: "Swiss mobile payment — instant",
    redirect: true,
    badge: "CH",
  },
  {
    id: "klarna",
    label: "Klarna",
    icon: <span className="pm-klarna-icon">K</span>,
    desc: "Buy now, pay later",
    redirect: true,
    badge: null,
  },
  {
    id: "postfinance",
    label: "PostFinance",
    icon: <Building2 size={20} />,
    desc: "PostFinance Card / E-Finance",
    redirect: true,
    badge: "CH",
  },
  {
    id: "bank_transfer",
    label: "Bank Transfer",
    icon: <Banknote size={20} />,
    desc: "Swiss IBAN — 1–3 business days",
    redirect: false,
    badge: null,
  },
];

// ─── Stripe Elements form (used for card; also mounts for TWINT/Klarna/PF) ───

function StripePaymentForm({ order, paymentMethod, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  /* */
  /*
  useEffect(() => {
    fetch(
      `${import.meta.env.REACT_APP_BACKEND_URL}/api/payments/createPaymentIntent`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          
          amount: Number(montantParam) * 100,
          currency: monnaie ? monnaie : "XOF",
          registrationId: inscriptionId,
          paymentMode: modePaiement,
          studentName: nomComplet,
          email: email,
          courseTitle: formationTitre,
          startDate: dateDebut,
          endDate: dateFin,
          place: lieu,
          participationMode: modeParticipation, 
        }),
      },
    )
      .then((res) => res.json())
      .then((data) => {
        setClientSecret(data.clientSecret);
        console.log(
          "Payment Intent created with clientSecret:",
          data.clientSecret,
        );
        //savePayment;
      });
  }, []);

  const options = clientSecret
    ? {
        clientSecret,
        appearance: {
          theme: "stripe",
        },
      }
    : undefined;
  */
  /* */

  useEffect(() => {
    if (!elements) return;
    elements.getElement(PaymentElement);

    const timer = setTimeout(() => {
      console.log(
        "mounted payment element:",
        elements.getElement(PaymentElement),
      );
    }, 1000);

    return () => clearTimeout(timer);
  }, [elements]);

  const isRedirect = PAYMENT_METHODS.find(
    (m) => m.id === paymentMethod,
  )?.redirect;

  const handleSubmit = async (e) => {
    /*
    if (!ready) {
      console.log("PaymentElement not ready");
      return;
    }
    */

    e.preventDefault();

    console.log("Console stripe:", elements?.getElement(PaymentElement)); //
    if (!stripe || !elements) return;

    setLoading(true);
    setErrMsg("");

    try {
      // For redirect-based methods (TWINT, Klarna, PostFinance):
      // Stripe redirects the user away, then back to return_url
      const returnUrl = `${window.location.origin}/order-success?orderId=${order._id}`;

      console.log("stripe", stripe);
      console.log("elements", elements);
      console.log("paymentElement", elements?.getElement(PaymentElement));

      //elements.getElement(PaymentElement);

      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
          payment_method_data: {
            billing_details: {
              name: `${order.shippingAddress.firstName} ${order.shippingAddress.lastName}`,
              email: order.userEmail || "",
              address: {
                line1: order.shippingAddress.street,
                city: order.shippingAddress.city,
                postal_code: order.shippingAddress.postalCode,
                country: order.shippingAddress.country || "CH",
              },
            },
          },
        },
        // For non-redirect methods (card) we handle success here
        redirect: isRedirect ? "always" : "if_required",
      });

      if (error) {
        if (error.type === "card_error" || error.type === "validation_error") {
          setErrMsg(error.message);
        } else {
          setErrMsg("An unexpected error occurred. Please try again.");
          console.error("[StripePaymentForm] error:", error);
        }
      } else {
        // Non-redirect: payment succeeded immediately
        onSuccess();
      }
    } catch (err) {
      setErrMsg("Payment failed. Please try again.");
      console.error("[StripePaymentForm] exception:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-form">
      <PaymentElement
        onReady={() => {
          console.log("PaymentElement mounted");
          setReady(true);
        }}
        options={{
          layout: "tabs",
          paymentMethodOrder: ["card", "twint", "klarna", "postfinance_card"],
          fields: {
            billingDetails: {
              name: "never", // we supply it from shipping address
              address: "auto",
            },
          },
        }}
      />

      {errMsg && (
        <div className="payment-error">
          <AlertCircle size={16} />
          {errMsg}
        </div>
      )}

      <div className="stripe-form-actions">
        <button
          type="button"
          className="btn-secondary cancel-btn"
          onClick={onCancel}
          disabled={!stripe || loading}
        >
          ← Change method
        </button>
        <button
          type="submit"
          className="btn-gold pay-btn"
          disabled={!stripe || loading}
        >
          {loading ? (
            <>
              <span
                className="spinner"
                style={{ width: 18, height: 18, borderWidth: 2 }}
              />{" "}
              Processing…
            </>
          ) : (
            <>
              <Lock size={15} /> Pay CHF {order?.total?.toFixed(2)}
            </>
          )}
        </button>
      </div>
    </form>
  );
}

// ─── Bank transfer instructions panel ─────────────────────────────────────────

function BankTransferPanel({ order, bankDetails, onCancel }) {
  const [copied, setCopied] = useState("");

  const copy = (val, label) => {
    navigator.clipboard.writeText(val).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(""), 2000);
    });
  };

  const account = bankDetails?.accounts?.[0];

  return (
    <div className="bank-transfer-panel">
      <div className="bank-transfer-header">
        <Banknote size={28} />
        <div>
          <h3>Bank Transfer Details</h3>
          <p>Transfer exactly the amount below to confirm your order.</p>
        </div>
      </div>

      <div className="bank-transfer-amount">
        <span>Amount due</span>
        <strong>CHF {order.total.toFixed(2)}</strong>
      </div>

      {account?.iban && (
        <div className="bank-field-grid">
          <BankField
            label="Bank"
            value={account.bankName || "Stripe Payments UK Ltd"}
          />
          <BankField
            label="IBAN"
            value={account.iban}
            onCopy={() => copy(account.iban, "iban")}
            copied={copied === "iban"}
          />
          {account.bic && (
            <BankField
              label="BIC / SWIFT"
              value={account.bic}
              onCopy={() => copy(account.bic, "bic")}
              copied={copied === "bic"}
            />
          )}
        </div>
      )}

      {bankDetails?.reference && (
        <div className="bank-reference">
          <AlertCircle size={14} />
          <span>
            Use reference{" "}
            <strong
              onClick={() => copy(bankDetails.reference, "ref")}
              style={{ cursor: "pointer", textDecoration: "underline" }}
            >
              {bankDetails.reference}
            </strong>{" "}
            — required for matching your payment.
          </span>
        </div>
      )}

      {bankDetails?.hostedUrl && (
        <a
          href={bankDetails.hostedUrl}
          target="_blank"
          rel="noreferrer"
          className="btn-secondary hosted-link"
        >
          <ExternalLink size={15} /> View full instructions
        </a>
      )}

      <div className="bank-transfer-note">
        <CheckCircle size={14} />
        Your order is reserved. We'll confirm it by email once we receive your
        transfer (typically 1–3 business days).
      </div>

      <button
        className="btn-secondary cancel-btn"
        onClick={onCancel}
        style={{ marginTop: 8 }}
      >
        ← Change method
      </button>
    </div>
  );
}

function BankField({ label, value, onCopy, copied }) {
  return (
    <div className="bank-field">
      <span className="bank-field-label">{label}</span>
      <div className="bank-field-value">
        <span>{value}</span>
        {onCopy && (
          <button className="copy-btn" onClick={onCopy} title={`Copy ${label}`}>
            {copied ? (
              <CheckCircle size={14} style={{ color: "var(--success)" }} />
            ) : (
              <Copy size={14} />
            )}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Redirect polling panel (TWINT / Klarna / PostFinance) ────────────────────

function RedirectPollingPanel({
  order,
  paymentIntentId,
  paymentMethod,
  onSuccess,
  onCancel,
}) {
  const navigate = useNavigate();
  const [status, setStatus] = useState("waiting"); // waiting | succeeded | failed
  const intervalRef = useRef(null);
  const methodLabel =
    PAYMENT_METHODS.find((m) => m.id === paymentMethod)?.label || paymentMethod;

  // Poll every 3 seconds for up to 5 minutes
  useEffect(() => {
    if (!paymentIntentId || paymentIntentId.startsWith("pi_mock")) {
      setStatus("succeeded");
      setTimeout(() => onSuccess(), 800);
      return;
    }

    let attempts = 0;
    intervalRef.current = setInterval(async () => {
      attempts++;
      try {
        const res = await api.get(`/payment/status/${paymentIntentId}`);
        const s = res.data.status;

        if (s === "succeeded") {
          clearInterval(intervalRef.current);
          setStatus("succeeded");
          setTimeout(() => onSuccess(), 1200);
        } else if (
          ["canceled", "cancelled", "requires_payment_method"].includes(s)
        ) {
          clearInterval(intervalRef.current);
          setStatus("failed");
        } else if (attempts > 100) {
          // 5 min
          clearInterval(intervalRef.current);
          setStatus("failed");
        }
      } catch (err) {
        console.error("Status poll error:", err);
      }
    }, 3000);

    return () => clearInterval(intervalRef.current);
  }, [paymentIntentId, onSuccess]);

  return (
    <div className="redirect-panel">
      {status === "waiting" && (
        <>
          <div className="redirect-spinner">
            <RefreshCw size={36} className="spin-icon" />
          </div>
          <h3>Waiting for {methodLabel} payment…</h3>
          <p>
            Complete your payment in the {methodLabel} window. This page will
            update automatically.
          </p>
          <button className="btn-secondary cancel-btn" onClick={onCancel}>
            ← Cancel & change method
          </button>
        </>
      )}
      {status === "succeeded" && (
        <>
          <CheckCircle
            size={48}
            style={{ color: "var(--success)", marginBottom: 12 }}
          />
          <h3>Payment confirmed!</h3>
          <p>Redirecting to your order…</p>
        </>
      )}
      {status === "failed" && (
        <>
          <AlertCircle
            size={48}
            style={{ color: "var(--error)", marginBottom: 12 }}
          />
          <h3>Payment not completed</h3>
          <p>Your payment was cancelled or failed. No charge was made.</p>
          <button className="btn-gold" onClick={onCancel}>
            Try again
          </button>
        </>
      )}
    </div>
  );
}

// ─── Main CheckoutPage ────────────────────────────────────────────────────────

export default function CheckoutPage() {
  const { items, subtotal, shippingCost, tax, total, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Step: 'shipping' | 'method' | 'payment'
  const [step, setStep] = useState("shipping");
  const [paymentMethod, setPaymentMethod] = useState("stripe_card");
  const [order, setOrder] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null); // { clientSecret, paymentIntentId, bankDetails, ... }
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [creatingIntent, setCreatingIntent] = useState(false);

  const [shipping, setShipping] = useState({
    firstName: user?.name?.split(" ")[0] || "",
    lastName: user?.name?.split(" ").slice(1).join(" ") || "",
    street: user?.address?.street || "",
    city: user?.address?.city || "",
    postalCode: user?.address?.postalCode || "",
    country: user?.address?.country || "CH",
    phone: user?.phone || "",
  });

  // Stripe Elements appearance options
  const stripeAppearance = {
    theme: "stripe",
    variables: {
      colorPrimary: "#1a4731",
      colorBackground: "#ffffff",
      colorText: "#1f2937",
      colorDanger: "#ef4444",
      fontFamily: "Inter, system-ui, sans-serif",
      spacingUnit: "4px",
      borderRadius: "8px",
      fontSizeBase: "15px",
    },
    rules: {
      ".Input": { border: "2px solid #e5e7eb", boxShadow: "none" },
      ".Input:focus": { border: "2px solid #1a4731", boxShadow: "none" },
      ".Label": { fontWeight: "600", color: "#374151" },
      ".Tab": { border: "2px solid #e5e7eb" },
      ".Tab--selected": { border: "2px solid #1a4731", color: "#1a4731" },
    },
  };

  // Step 1: validate shipping form → go to method selection
  const handleShippingSubmit = (e) => {
    e.preventDefault();
    setStep("method");
  };

  // Step 2: method selected → create order + payment intent
  const handleProceedToPayment = async () => {
    setCreatingOrder(true);
    try {
      // 1. Create the order
      const orderRes = await api.post("/orders", {
        items: items.map((i) => ({ product: i._id, quantity: i.quantity })),
        shippingAddress: shipping,
        paymentMethod,
      });
      const newOrder = { ...orderRes.data.order, userEmail: user.email };
      setOrder(newOrder);

      // 2. Create the payment intent for all Stripe-based methods
      if (paymentMethod !== "bank_transfer") {
        setCreatingIntent(true);
      }
      const intentRes = await api.post("/payment/create-intent", {
        orderId: newOrder._id,
        paymentMethodType: paymentMethod,
      });
      setPaymentResult(intentRes.data);
      setStep("payment");
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          "Could not initialise payment. Please try again.",
      );
      console.error("[handleProceedToPayment]", err);
    } finally {
      setCreatingOrder(false);
      setCreatingIntent(false);
    }
  };

  // Called by any payment panel when payment is complete
  const handlePaymentSuccess = useCallback(() => {
    clearCart();
    navigate(`/order-success?orderId=${order._id}`);
  }, [clearCart, navigate, order]);

  // Cancel / go back — also cancels the pending intent on the server
  const handleCancelPayment = async () => {
    if (paymentResult?.paymentIntentId && !paymentResult.mock) {
      api
        .post("/payment/cancel", {
          paymentIntentId: paymentResult.paymentIntentId,
        })
        .catch(() => {});
    }
    setOrder(null);
    setPaymentResult(null);
    setStep("method");
  };

  // Empty cart guard
  if (items.length === 0 && !order) {
    return (
      <div className="checkout-empty">
        <h2>Your cart is empty</h2>
        <button className="btn-primary" onClick={() => navigate("/shop")}>
          Shop Now
        </button>
      </div>
    );
  }

  const selectedMethod = PAYMENT_METHODS.find((m) => m.id === paymentMethod);
  const isMockMode = paymentResult?.mock;
  const isRedirectMethod = selectedMethod?.redirect && !isMockMode;

  return (
    <div className="checkout-page">
      <div className="checkout-container container">
        {/* ── Main column ───────────────────────────────────────────────── */}
        <div className="checkout-main">
          {/* Progress steps */}
          <div className="checkout-steps">
            {["shipping", "method", "payment"].map((s, i) => (
              <React.Fragment key={s}>
                <div
                  className={`step ${
                    step === s
                      ? "active"
                      : ["shipping", "method", "payment"].indexOf(step) > i
                        ? "done"
                        : ""
                  }`}
                >
                  <span>
                    {["shipping", "method", "payment"].indexOf(step) > i
                      ? "✓"
                      : i + 1}
                  </span>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </div>
                {i < 2 && <div className="step-divider" />}
              </React.Fragment>
            ))}
          </div>

          {/* ── Step 1: Shipping ──────────────────────────────────────── */}
          {step === "shipping" && (
            <div className="checkout-section">
              <h2>Shipping Address</h2>
              <form onSubmit={handleShippingSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      className="form-control"
                      value={shipping.firstName}
                      onChange={(e) =>
                        setShipping({ ...shipping, firstName: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      className="form-control"
                      value={shipping.lastName}
                      onChange={(e) =>
                        setShipping({ ...shipping, lastName: e.target.value })
                      }
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Street Address</label>
                  <input
                    className="form-control"
                    value={shipping.street}
                    onChange={(e) =>
                      setShipping({ ...shipping, street: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Postal Code</label>
                    <input
                      className="form-control"
                      maxLength={10}
                      value={shipping.postalCode}
                      onChange={(e) =>
                        setShipping({ ...shipping, postalCode: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>City</label>
                    <input
                      className="form-control"
                      value={shipping.city}
                      onChange={(e) =>
                        setShipping({ ...shipping, city: e.target.value })
                      }
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>
                    Phone <span className="optional">(optional)</span>
                  </label>
                  <input
                    className="form-control"
                    type="tel"
                    value={shipping.phone}
                    onChange={(e) =>
                      setShipping({ ...shipping, phone: e.target.value })
                    }
                  />
                </div>
                <button type="submit" className="btn-gold full-btn">
                  Continue to Payment Method <ArrowRight size={18} />
                </button>
              </form>
            </div>
          )}

          {/* ── Step 2: Choose payment method ─────────────────────────── */}
          {step === "method" && (
            <div className="checkout-section">
              <button className="back-btn" onClick={() => setStep("shipping")}>
                ← Back to shipping
              </button>
              <h2>Payment Method</h2>
              <p className="method-subtitle">
                All payments are secured and encrypted.
              </p>

              <div className="payment-methods">
                {PAYMENT_METHODS.map((pm) => (
                  <label
                    key={pm.id}
                    className={`payment-method-card ${paymentMethod === pm.id ? "selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={pm.id}
                      checked={paymentMethod === pm.id}
                      onChange={() => setPaymentMethod(pm.id)}
                    />
                    <span className="pm-icon">{pm.icon}</span>
                    <span className="pm-info">
                      <strong>{pm.label}</strong>
                      <span>{pm.desc}</span>
                    </span>
                    {pm.badge && <span className="pm-badge">{pm.badge}</span>}
                    {pm.redirect && (
                      <span className="pm-redirect-tag">Redirect</span>
                    )}
                  </label>
                ))}
              </div>

              <button
                className="btn-gold full-btn"
                onClick={handleProceedToPayment}
                disabled={creatingOrder}
              >
                {creatingOrder ? (
                  <>
                    <span
                      className="spinner"
                      style={{ width: 18, height: 18, borderWidth: 2 }}
                    />{" "}
                    Preparing order…
                  </>
                ) : (
                  <>
                    Proceed to Pay CHF {total.toFixed(2)}{" "}
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </div>
          )}

          {/* ── Step 3: Payment ───────────────────────────────────────── */}
          {step === "payment" && order && paymentResult && (
            <div className="checkout-section">
              <h2>
                {selectedMethod?.icon && (
                  <span className="step-pm-icon">{selectedMethod.icon}</span>
                )}
                {selectedMethod?.label}
              </h2>

              {/* Loading intent */}
              {creatingIntent && (
                <div className="intent-loading">
                  <span className="spinner" />
                  Connecting to payment provider…
                </div>
              )}

              {/* Mock mode banner */}
              {isMockMode && (
                <div className="mock-banner">
                  <AlertCircle size={16} />
                  <span>
                    <strong>Dev mode:</strong> No real Stripe key — click "Pay"
                    to simulate success.
                  </span>
                </div>
              )}

              {/* Bank transfer */}
              {paymentMethod === "bank_transfer" && (
                <BankTransferPanel
                  order={{ ...order, total }}
                  bankDetails={paymentResult.bankDetails}
                  onCancel={handleCancelPayment}
                  onSuccess={handlePaymentSuccess}
                />
              )}

              {/* Mock payment (no Stripe key) */}
              {isMockMode && paymentMethod !== "bank_transfer" && (
                <div className="mock-pay">
                  <p>
                    Simulated payment for{" "}
                    <strong>{selectedMethod?.label}</strong>
                  </p>
                  <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
                    <button
                      className="btn-secondary cancel-btn"
                      onClick={handleCancelPayment}
                    >
                      ← Back
                    </button>
                    <button
                      className="btn-gold pay-btn"
                      onClick={handlePaymentSuccess}
                    >
                      <Lock size={15} /> Simulate Success — CHF{" "}
                      {total.toFixed(2)}
                    </button>
                  </div>
                </div>
              )}

              {/* Real Stripe Elements */}
              {!isMockMode &&
                paymentMethod !== "bank_transfer" &&
                paymentResult?.clientSecret && (
                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret: paymentResult.clientSecret,
                      appearance: stripeAppearance,
                    }}
                  >
                    {/* After a redirect method (TWINT etc.) the user returns here — poll status */}
                    {isRedirectMethod && paymentResult.paymentIntentId ? (
                      /* We still mount StripePaymentForm to trigger the redirect.
                       After redirect & return, the URL will have status params and
                       Stripe will handle it via return_url automatically. */
                      <StripePaymentForm
                        order={{ ...order, userEmail: user.email }}
                        paymentMethod={paymentMethod}
                        onSuccess={handlePaymentSuccess}
                        onCancel={handleCancelPayment}
                      />
                    ) : (
                      <StripePaymentForm
                        order={{ ...order, userEmail: user.email }}
                        paymentMethod={paymentMethod}
                        onSuccess={handlePaymentSuccess}
                        onCancel={handleCancelPayment}
                      />
                    )}
                  </Elements>
                )}
            </div>
          )}
        </div>

        {/* ── Order summary sidebar ─────────────────────────────────────────── */}
        <aside className="checkout-summary">
          <h3>Order Summary</h3>
          <div className="summary-items">
            {items.map((item) => (
              <div key={item._id} className="summary-item">
                <img
                  src={
                    item.thumbnail ||
                    "https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=80"
                  }
                  alt={item.name}
                />
                <div>
                  <p>{item.name}</p>
                  <span>× {item.quantity}</span>
                </div>
                <span>CHF {(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="summary-totals">
            <div className="summary-row">
              <span>Subtotal</span>
              <span>CHF {subtotal.toFixed(2)}</span>
            </div>
            <div className="summary-row">
              <span>Shipping</span>
              <span>
                {shippingCost === 0 ? (
                  <span style={{ color: "var(--success)", fontWeight: 700 }}>
                    Free
                  </span>
                ) : (
                  `CHF ${shippingCost.toFixed(2)}`
                )}
              </span>
            </div>
            <div className="summary-row">
              <span>VAT (7.7%)</span>
              <span>CHF {tax.toFixed(2)}</span>
            </div>
            {subtotal < 50 && (
              <p className="free-ship-hint">
                Add CHF {(50 - subtotal).toFixed(2)} more for free shipping
              </p>
            )}
            <div className="summary-total">
              <span>Total</span>
              <span>CHF {total.toFixed(2)}</span>
            </div>
          </div>
          <div className="secure-badge">
            <Lock size={13} /> Secure · Encrypted · PCI-compliant
          </div>
          <div className="payment-logos">
            <span>VISA</span>
            <span>MC</span>
            <span style={{ color: "var(--gold-dark)" }}>TWINT</span>
            <span>K</span>
            <span>PF</span>
          </div>
        </aside>
      </div>
    </div>
  );
}
